<script lang="ts">

import { onMount } from "svelte";
import { link } from "svelte-routing";
import { fade } from "svelte/transition";
import { quintOut } from "svelte/easing";
import { fetchPeople } from "../services/people";
import type { Person } from "../structs";

// Fetch people data from people.json and sort and categorize it
const peopleDataFetch = new Promise(async (resolve: (data: Person[]) => void) => {
    let data = await fetchPeople();
    let sortedItemsByPosition = [];
    
    // Categorize people based on position
    data.forEach(item => {
        const position = (item.position) ? item.position : "Uncategorized";

        let yearItem = sortedItemsByPosition.find(o => o.category === position);
        const itemIndex = sortedItemsByPosition.indexOf(yearItem);
        yearItem ??= { category: position, data: [] };
        yearItem.data.push(item);

        (itemIndex < 0) ? sortedItemsByPosition.push(yearItem) : sortedItemsByPosition[itemIndex] = yearItem;
    });

    // This array is sorted by priority (from highest to lowest) 
    let prioritizedCategoryKeyWords = [
        "Faculty", 
        "Professor",
        "Staff",
        "Associate",
        "Fellow",
        "PhD",
        "MASc",
        "Guest"
    ];

    // Prioritize certain categories so the order of categories doesn't change whenn new person is added
    sortedItemsByPosition.sort((a, b) => {
        return prioritizedCategoryKeyWords.slice().reverse().findIndex(value => {
            return (new RegExp(value.toLowerCase())).test(b.category.toLowerCase());
        }) - prioritizedCategoryKeyWords.slice().reverse().findIndex(value => {
            return (new RegExp(value.toLowerCase())).test(a.category.toLowerCase());
        });
    });

    resolve(sortedItemsByPosition);
});


let searchTerm = "";
let peopleData;

$: filteredResults = (peopleData) ? peopleData.map(list => {
    return {
        category: list.category,
        data: list.data.filter(item => item.name.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0)
    }
}).filter(list => list.data.length > 0) : null;


onMount(async () => {
    peopleData = await peopleDataFetch;
});

</script>




<div class="top-background" style="background-image:url(images/backgrounds/people.jpg);">
    <h1 class="page-title">The LCML Team</h1>
    <!-- Credit to the photographer (not required but still giving it) -->
    <div class="credit">
        Photo by <a href="https://unsplash.com/@dylandgillis?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText">Dylan Gillis</a>
    </div>
</div>

<section id="people">

    <div class="content">
        <!-- Search Bar -->
        <input bind:value={searchTerm} class="search-box" type="text" placeholder="Search...">
        <div class="list-container">
            <!-- Search Results -->
            {#await peopleDataFetch}
                <!-- Loading Animatin -->
                <div class="loading-cube-grid">
                    <div class="sk-cube sk-cube1"></div>
                    <div class="sk-cube sk-cube2"></div>
                    <div class="sk-cube sk-cube3"></div>
                    <div class="sk-cube sk-cube4"></div>
                    <div class="sk-cube sk-cube5"></div>
                    <div class="sk-cube sk-cube6"></div>
                    <div class="sk-cube sk-cube7"></div>
                    <div class="sk-cube sk-cube8"></div>
                    <div class="sk-cube sk-cube9"></div>
                </div>
            {:then _}
                <div in:fade={{ easing: quintOut, duration: 1000 }}>
                    {#if filteredResults}
                        {#each filteredResults as category}
                            <h5 class="category-name">{category.category}s</h5>
                            <ul>
                                {#each category.data as person, index}
                                    <li>
                                        {#if person.imageSrc}
                                            <img src={person.imageSrc} alt="{person.name}'s profile photo"/>
                                        {:else}
                                            <img src="/images/people/default.jpg" alt="{person.name}'s default profile photo"/>
                                        {/if}
                                        <div class="details">
                                            <div class="name">{@html person.name}</div>
                                            <div class="position">{@html person.position}</div>
                                        </div>
                                        {#if person.UbcProfileLink}
                                            <a href={person.UbcProfileLink} target="_blank" class="action-button">View Profile</a>
                                        {:else}
                                            <a href={`/profile/${person.id}`} class="action-button" use:link>More Details</a>
                                        {/if}
                                    </li>
                                {/each}
                            </ul>
                        {/each}

                        <!-- If search result cant match anything -->
                        {#if filteredResults.length < 1}
                            <div class="no-results-container">
                                <img src="images/icons/no-results.png" alt="no results">
                                <h3>Couldn't find anything for "{searchTerm}"</h3>
                            </div>
                        {/if}
                    {/if}
                </div>
            {/await}
        </div>
    </div>

</section>




<style lang="sass">

@import "../consts.sass"
@include page-header()
@include search-box()
@include action-button()
@include loading-spinner()
@include no-search-results-text()

.content
    padding: 2em
    box-sizing: border-box

    @media only screen and (min-width: 1200px) 
        padding-right: 10vw
        padding-left: 10vw

    .list-container
        h5.category-name
            font-size: 2em
            color: #333
        
        ul
            list-style-type: none
            display: flex
            flex-direction: row
            flex-flow: wrap
            margin: 0
            margin-top: 2em

            li
                display: flex
                flex-direction: column
                justify-content: space-between
                width: 15em
                border-radius: 0.3em
                margin-bottom: 2.8em

                &:not(:last-child)
                    margin-right: 2.5em

                img
                    border-radius: 0.2em


                .details
                    margin: 1em 0

                    .name
                        font-size: 1.5em
                        font-weight: bold

                    .position
                        font-size: 1em
                    



</style>